import { RefObject, useEffect, useState } from 'react'

/**
 *
 * useScrollToSearch — A React hook that monitors a referenced search bar
 * and detects when it scrolls out of view.
 * It’s primarily used to display a compact header search on mobile devices when
 * the main search bar is no longer visible.
 * Returns true when the element is out of the viewport, and false otherwise.
 *
 */
export function useScrollToSearch<T extends Element>(ref: RefObject<T>, enabled: boolean): boolean {
    const [outOfView, setOutOfView] = useState(false)

    useEffect(() => {
        if (!enabled) {
            setOutOfView(false)
            return
        }
        const target = ref.current
        if (!target) {
            setOutOfView(false)
            return
        }

        const observer = new IntersectionObserver(
            entries => {
                const entry = entries[0]
                setOutOfView(!entry.isIntersecting)
            },
            { threshold: 0 },
        )

        observer.observe(target)
        return () => observer.disconnect()
    }, [enabled, ref])

    return outOfView
}
